import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  serverTimestamp,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  increment
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Debug log to check if environment variables are loaded
console.log('Firebase Config:', {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY ? 'Exists' : 'Missing',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ? 'Exists' : 'Missing',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ? 'Exists' : 'Missing',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ? 'Exists' : 'Missing',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ? 'Exists' : 'Missing',
  appId: process.env.REACT_APP_FIREBASE_APP_ID ? 'Exists' : 'Missing'
});

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Get Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

/**
 * Creates or updates a user profile in the 'users' collection.
 * @param {import('firebase/auth').User} user The authenticated user object from Firebase Auth.
 */
const manageUserProfile = async (user) => {
  if (!user) return;

  const userDocRef = doc(db, 'users', user.uid);
  const userDocSnap = await getDoc(userDocRef);

  const userData = {
    email: user.email,
    displayName: user.displayName || null,
    lastSignIn: serverTimestamp(),
  };

  if (!userDocSnap.exists()) {
    // New user, create the document with createdAt
    userData.createdAt = serverTimestamp();
    userData.lastDesignCreatedAt = null; // Initialize for monthly design limit tracking
    
    // Increment user count for new users
    try {
      const userCountRef = doc(db, 'user-count', 'total');
      await updateDoc(userCountRef, {
        count: increment(1)
      }).catch(async (error) => {
        // If the document doesn't exist, create it
        if (error.code === 'not-found') {
          await setDoc(userCountRef, { count: 1 });
        } else {
          throw error;
        }
      });
      console.log('User count incremented for new user:', user.uid);
    } catch (error) {
      console.error('Error updating user count:', error);
    }
  }

  try {
    // Use setDoc with merge:true to create or update
    await setDoc(userDocRef, userData, { merge: true });
    console.log('User profile managed successfully:', user.uid);
  } catch (error) {
    console.error('Error managing user profile:', error);
  }
};

// --- Google Auth ---
const googleProvider = new GoogleAuthProvider();
export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);

// --- Passwordless Email Auth ---
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

/**
 * Sends a passwordless sign-in link to the provided email.
 * @param {string} email The user's email address.
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export const sendPasswordlessLink = async (email) => {
  const actionCodeSettings = {
    url: `${window.location.origin}/login?auth=passwordless`, // Redirect here after clicking the link
    handleCodeInApp: true,
  };

  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save the email locally to use when the user returns.
    window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
    return { success: true };
  } catch (error) {
    console.error("Error sending passwordless link:", error);
    return { success: false, error };
  }
};

/**
 * Checks if the current URL is a sign-in link and completes the sign-in process.
 * @param {string} providedEmail Optional email if user opened link on different device
 * @param {string} emailLinkUrl Optional full URL of the sign-in link (use when link was opened then user navigated, e.g. to /confirm-email)
 * @returns {Promise<{success: boolean, user?: import('firebase/auth').User, error?: any, needsEmail?: boolean}>}
 */
export const completePasswordlessSignIn = async (providedEmail = null, emailLinkUrl = null) => {
  const urlToUse = emailLinkUrl || window.location.href;
  if (!isSignInWithEmailLink(auth, urlToUse)) {
    return { success: false }; // Not a sign-in link
  }

  let email = providedEmail || window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
  if (!email) {
    // The user opened the link on a different device.
    // Return needsEmail flag so the UI can handle it gracefully.
    return { success: false, needsEmail: true };
  }

  try {
    const result = await signInWithEmailLink(auth, email, urlToUse);
    window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
    return { success: true, user: result.user };
  } catch (error) {
    console.error("Error completing passwordless sign-in:", error);
    return { success: false, error };
  }
};

// Log auth state changes for debugging and manage user profile
auth.onAuthStateChanged((user) => {
  console.log('Auth state changed:', {
    isAuthenticated: !!user
  });

  if (user) {
    // If user is authenticated, manage their profile in Firestore
    manageUserProfile(user);
  }
});

export default app;
